import { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Grid,
    Card,
    CardActionArea,
    CardContent,
    CardMedia,
    CircularProgress,
    Alert,
    Typography,
    Stack,
    Chip,
    IconButton,
    Dialog,
    DialogContent,
    Tooltip,
} from '@mui/material';
import {
    PhotoLibrary as PhotoLibraryIcon,
    Person as PersonIcon,
    Delete as DeleteIcon,
    Language as LanguageIcon,
    Close as CloseIcon,
    ZoomIn as ZoomInIcon,
    ZoomOut as ZoomOutIcon,
    RestartAlt as RestartAltIcon,
    OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { useGetScreenshotsQuery, useDeleteScreenshotMutation } from '@/store/apiSlice';
import { ENDPOINTS } from '@/config/endpoints';
import { useI18nContext } from '@/i18n/i18n-react';
import { AdminPageHeader } from '@/components/admin/AdminPageHeader';
import { RefetchButton } from '@/components/admin/RefetchButton';
import { useAppDialog } from '@/components/dialogs/AppDialog';
import { getApiErrorMessage } from '@/utils/apiError';
import type { ScreenshotMeta } from '@/types';

const API_BASE = import.meta.env.VITE_API_BASE_URL || '/';
const imageUrl = (id: number) => API_BASE + ENDPOINTS.screenshotImage(id);

const formatDate = (value: string): string => {
    const date = new Date(value.replace(' ', 'T'));
    return isNaN(date.getTime()) ? value : date.toLocaleString();
};

const AdminScreenshotsPage = () => {
    const { LL } = useI18nContext();
    const { alert, confirm } = useAppDialog();
    const { data: screenshots, isLoading, isFetching, error, refetch } = useGetScreenshotsQuery();
    const [deleteScreenshot] = useDeleteScreenshotMutation();
    const [preview, setPreview] = useState<ScreenshotMeta | null>(null);
    const [zoom, setZoom] = useState(1);

    const ZOOM_MIN = 1;
    const ZOOM_MAX = 5;
    const clampZoom = (value: number) => Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, value));

    // Reset zoom whenever a different screenshot is opened.
    useEffect(() => {
        setZoom(1);
    }, [preview?.id]);

    const handleWheelZoom = useCallback((e: React.WheelEvent) => {
        e.preventDefault();
        setZoom((z) => clampZoom(z + (e.deltaY < 0 ? 0.25 : -0.25)));
    }, []);

    const handleDelete = async (shot: ScreenshotMeta) => {
        if (!(await confirm(LL.adminScreenshotsPage.deleteConfirm()))) return;
        try {
            await deleteScreenshot(shot.id).unwrap();
            if (preview?.id === shot.id) setPreview(null);
        } catch (err) {
            await alert(getApiErrorMessage(err));
        }
    };

    return (
        <>
            <AdminPageHeader
                icon={<PhotoLibraryIcon />}
                title={LL.adminScreenshotsPage.title()}
                subtitle={LL.adminScreenshotsPage.subtitle()}
                refetchButton={<RefetchButton onRefetch={refetch} isRefetching={isFetching} />}
            />

            <Box sx={{ px: 3, pb: 6 }}>
                {isLoading ? (
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            minHeight: 400,
                        }}
                    >
                        <CircularProgress />
                    </Box>
                ) : error ? (
                    <Alert severity="error">{LL.adminScreenshotsPage.failedToLoad()}</Alert>
                ) : !screenshots || screenshots.length === 0 ? (
                    <Alert severity="info">{LL.adminScreenshotsPage.empty()}</Alert>
                ) : (
                    <Grid container spacing={2}>
                        {screenshots.map((shot) => (
                            <Grid key={shot.id} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                                <Card
                                    sx={{
                                        height: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                    }}
                                >
                                    <CardActionArea onClick={() => setPreview(shot)}>
                                        <CardMedia
                                            component="img"
                                            image={imageUrl(shot.id)}
                                            alt={shot.url ?? String(shot.id)}
                                            sx={{
                                                height: 180,
                                                objectFit: 'cover',
                                                objectPosition: 'top',
                                                bgcolor: 'action.hover',
                                            }}
                                        />
                                    </CardActionArea>
                                    <CardContent sx={{ flex: 1 }}>
                                        <Stack
                                            direction="row"
                                            spacing={1}
                                            sx={{ alignItems: 'center', mb: 1 }}
                                        >
                                            <PersonIcon fontSize="small" color="primary" />
                                            <Typography variant="subtitle2" noWrap>
                                                {shot.resourceName ??
                                                    LL.adminScreenshotsPage.unknownUser()}
                                            </Typography>
                                        </Stack>
                                        {(shot.browser || shot.userAgent) && (
                                            <Stack
                                                direction="row"
                                                spacing={0.5}
                                                sx={{ alignItems: 'center', mb: 1, minWidth: 0 }}
                                            >
                                                <LanguageIcon
                                                    fontSize="small"
                                                    sx={{ color: 'text.secondary' }}
                                                />
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                    noWrap
                                                    title={shot.userAgent ?? undefined}
                                                >
                                                    {shot.browser
                                                        ? `${shot.browser} — ${shot.userAgent ?? ''}`
                                                        : shot.userAgent}
                                                </Typography>
                                            </Stack>
                                        )}
                                        <Stack
                                            direction="row"
                                            spacing={1}
                                            sx={{
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                            }}
                                        >
                                            <Typography variant="caption" color="text.secondary">
                                                {formatDate(shot.createdAt)}
                                            </Typography>
                                            <Stack
                                                direction="row"
                                                spacing={1}
                                                sx={{ alignItems: 'center' }}
                                            >
                                                {(shot.width != null || shot.height != null) && (
                                                    <Chip
                                                        label={`${shot.width}px × ${shot.height}px`}
                                                        size="small"
                                                        variant="outlined"
                                                        sx={{ height: 20, fontSize: '0.7rem' }}
                                                    />
                                                )}
                                                <Tooltip title={LL.adminScreenshotsPage.delete()}>
                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        onClick={() => handleDelete(shot)}
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </Stack>
                                        </Stack>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                )}
            </Box>

            {/* Fullscreen preview with zoom */}
            <Dialog open={!!preview} onClose={() => setPreview(null)} maxWidth="xl" fullWidth>
                <DialogContent sx={{ p: 0, position: 'relative', bgcolor: 'black' }}>
                    {/* Toolbar */}
                    <Stack
                        direction="row"
                        spacing={0.5}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8,
                            zIndex: 2,
                            alignItems: 'center',
                            bgcolor: 'rgba(0,0,0,0.5)',
                            borderRadius: 2,
                            px: 0.5,
                            color: 'white',
                        }}
                    >
                        <Tooltip title={LL.adminScreenshotsPage.zoomOut()}>
                            <span>
                                <IconButton
                                    size="small"
                                    sx={{ color: 'white' }}
                                    disabled={zoom <= ZOOM_MIN}
                                    onClick={() => setZoom((z) => clampZoom(z - 0.25))}
                                >
                                    <ZoomOutIcon />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Typography
                            variant="caption"
                            sx={{ minWidth: 44, textAlign: 'center', userSelect: 'none' }}
                        >
                            {Math.round(zoom * 100)}%
                        </Typography>
                        <Tooltip title={LL.adminScreenshotsPage.zoomIn()}>
                            <span>
                                <IconButton
                                    size="small"
                                    sx={{ color: 'white' }}
                                    disabled={zoom >= ZOOM_MAX}
                                    onClick={() => setZoom((z) => clampZoom(z + 0.25))}
                                >
                                    <ZoomInIcon />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Tooltip title={LL.adminScreenshotsPage.resetZoom()}>
                            <span>
                                <IconButton
                                    size="small"
                                    sx={{ color: 'white' }}
                                    disabled={zoom === 1}
                                    onClick={() => setZoom(1)}
                                >
                                    <RestartAltIcon />
                                </IconButton>
                            </span>
                        </Tooltip>
                        {preview && (
                            <Tooltip title={LL.adminScreenshotsPage.openOriginal()}>
                                <IconButton
                                    size="small"
                                    sx={{ color: 'white' }}
                                    component="a"
                                    href={imageUrl(preview.id)}
                                    target="_blank"
                                    rel="noopener"
                                >
                                    <OpenInNewIcon />
                                </IconButton>
                            </Tooltip>
                        )}
                        <Tooltip title={LL.common.close()}>
                            <IconButton
                                size="small"
                                sx={{ color: 'white' }}
                                onClick={() => setPreview(null)}
                            >
                                <CloseIcon />
                            </IconButton>
                        </Tooltip>
                    </Stack>

                    {/* Scrollable, zoomable image area */}
                    <Box
                        onWheel={handleWheelZoom}
                        sx={{
                            height: '85vh',
                            overflow: 'auto',
                            display: 'flex',
                        }}
                    >
                        {preview && (
                            <Box
                                component="img"
                                src={imageUrl(preview.id)}
                                alt={preview.userAgent ?? String(preview.id)}
                                sx={{
                                    display: 'block',
                                    // `margin: auto` keeps the image centered and — unlike
                                    // justify/align center — lets you scroll to every edge
                                    // once it is zoomed past the container.
                                    margin: 'auto',
                                    maxWidth: '100%',
                                    maxHeight: '85vh',
                                    width: 'auto',
                                    height: 'auto',
                                    objectFit: 'contain',
                                    transform: `scale(${zoom})`,
                                    transformOrigin: 'center center',
                                    transition: 'transform 0.12s ease-out',
                                    cursor: zoom >= ZOOM_MAX ? 'zoom-out' : 'zoom-in',
                                }}
                                onClick={() =>
                                    setZoom((z) => (z >= ZOOM_MAX ? 1 : clampZoom(z + 1)))
                                }
                            />
                        )}
                    </Box>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default AdminScreenshotsPage;
